import _ from 'lodash';
import { IReactionDisposer, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Component } from 'react';

import { controller } from '../../core/Controllers/OrmController';
import { OrmStoreType } from '../../core/Stores/DirectoryStore';
import { OrmUserStoreType } from '../../core/Stores/OrmUserStore';
import { FormAlertStoreType } from '../../shared/Alerts/FormAlertStore';
import NatDirectoryFormContainer from '../../shared/Components/NatDirectoryFormContainer';
import { NavigationContainerStoreType } from '../../shared/ContainersStores/NavigationContainerStore';
import { catalog } from '../../shared/support/Catalog';
import { FormContainerPropsType, PartnerContractType } from '../../shared/support/modelTypes';
import { UseLocation } from '../../shared/support/useLocationHoC';
import PartnerContractForm from './PartnerContractForm';
import { PartnerContractFormStoreType } from './PartnerContractFormStore';

interface PropsType extends FormContainerPropsType<PartnerContractFormStoreType> {}

interface InjectedProps extends PropsType {
	navigationContainerStore: NavigationContainerStoreType;
	userStore: OrmUserStoreType;
	directoryStore: OrmStoreType;
	formAlertStore: FormAlertStoreType;
}

@inject('navigationContainerStore', 'userStore', 'directoryStore', 'formAlertStore')
@observer
class PartnerContractFormContainer extends Component<PropsType> {
	formReactions: Array<IReactionDisposer>;
	constructor(props: PropsType) {
		super(props);
		this.formReactions = [];
		this.loadPage = this.loadPage.bind(this);
		this.makeReactions = this.makeReactions.bind(this);
		this.resetFormReactions = this.resetFormReactions.bind(this);
		this.fillEmptyRelations = this.fillEmptyRelations.bind(this);
		this.prepareModelForPost = this.prepareModelForPost.bind(this);
		this.validate = this.validate.bind(this);
		this.resetData = this.resetData.bind(this);
	}
	get injected() {
		return this.props as InjectedProps;
	}
	makeReactions() {
		this.resetFormReactions();
		_.forIn(this.props.store.model, (value, key) => {
			if (key !== 'isChanged') {
				this.formReactions.push(
					reaction(
						() => this.props.store.model[key],
						(value, previousValue) => {
							if (key !== 'accountingMethod' && key !== 'type' && key !== 'status' && key !== 'priceType' && key !== 'organization') {
								if (value !== previousValue) {
									this.props.store.setValue(this.props.store, 'isChanged', true);
								}
							}
						}
					)
				);
			}
		});
		this.formReactions.push(
			reaction(
				() => this.props.store.model.accountingMethodId,
				(value) => {
					if (_.isEmpty(value)) {
						this.props.store.setValue(this.props.store.model, 'accountingMethodId', 'partner_settlement_accounting_methods.by_contracts');
						this.props.store.setValue(this.props.store.model, 'accountingMethod', {
							id: 'partner_settlement_accounting_methods.by_contracts',
							keyId: 'by_contracts',
							ownerId: 'partner_settlement_accounting_methods',
							name: { ru: 'По договору' },
							description: null,
							predefined: true,
							predefinedName: 'PartnerSettlementAccountingMethodsByContracts',
							predefinedVersion: 0
						});
					}
				}
			)
		);
		this.formReactions.push(
			reaction(
				() => this.props.store.model.statusId,
				(value) => {
					if (_.isEmpty(value)) {
						this.props.store.setValue(this.props.store.model, 'statusId', 'partner_contract_statuses.invalid');
						this.props.store.setValue(this.props.store.model, 'status', {
							id: 'partner_contract_statuses.invalid',
							keyId: 'invalid',
							ownerId: 'partner_contract_statuses',
							name: { ru: 'Не действует' },
							description: null,
							predefined: true,
							predefinedName: 'PartnerContractStatusesInvalid',
							predefinedVersion: 0
						});
					}
				}
			)
		);
		this.formReactions.push(
			reaction(
				() => this.props.store.isChanged,
				(value, previousValue) => {
					if (value !== previousValue) {
						catalog.generateTitle(this.props.navigationRoute, 'OrmCatalog', 'form');
					}
				}
			)
		);
	}
	loadPage(elementId: string, findModel: (elementId: string) => void) {
		document.title = 'Журнал';
		if (elementId !== 'new') {
			findModel(elementId);
		} else {
			const query = this.props.query;
			const partnerId = query.get('partnerId');
			const standart = query.get('standart');
			this.props.store.setValue(this.props.store, 'responseCode', 200);
			catalog.setConstants(this.props.store, this.props.store.requiredToFill);
			catalog.generateTitle(this.props.navigationRoute, 'OrmCatalog', 'form');
			if (partnerId !== null) {
				let partnersFilter = {
					where: { id: partnerId },
					limit: 1
				};
				controller
					.findAll('partners', partnersFilter)
					.then((data) => {
						if (!_.isEmpty(data)) {
							this.props.store.setValue(this.props.store.model, 'partnerId', data[0].id);
						}
					})
					.catch((error) => {
						catalog.handleNatError(error);
					});
			}
			if (standart !== null) {
				this.props.store.setValue(this.props.store.model, 'standart', Boolean(standart));
			}
			this.props.store.setValue(this.props.store, 'isChanged', true);
		}
	}
	fillEmptyRelations(model: PartnerContractType) {
		if (model.accountingMethodId === 'partner_settlement_accounting_methods.by_contracts' && _.isEmpty(model.accountingMethod)) {
			model.accountingMethod = _.cloneDeep(this.props.store.model.accountingMethod);
		}
		if (model.statusId === 'partner_contract_statuses.invalid' && _.isEmpty(model.status)) {
			model.status = _.cloneDeep(this.props.store.model.status);
		}
		if (!_.isEmpty(model.contractDate)) {
			model.contractDate = new Date(model.contractDate as any);
		}
		if (!_.isEmpty(model.startedAt)) {
			model.startedAt = new Date(model.startedAt as any);
		}
		if (!_.isEmpty(model.expiredAt)) {
			model.expiredAt = new Date(model.expiredAt as any);
		}
		return model;
	}
	prepareModelForPost(model: PartnerContractType) {
		delete model.priceType;
		delete model.organization;
		delete model.type;
		delete model.status;
		delete model.accountingMethod;
		return model;
	}
	validate() {
		return !_.isEmpty(this.props.store.model.name[this.injected.directoryStore.models.language]) && !_.isEmpty(this.props.store.model.typeId);
	}
	resetFormReactions() {
		this.formReactions.forEach((dispose) => dispose());
		this.formReactions = [];
	}
	resetData() {
		this.props.store.setValue(this.props.store, 'list', []);
	}
	render() {
		return (
			<NatDirectoryFormContainer
				index={this.props.index}
				navigationRoute={this.props.navigationRoute}
				replace={this.props.replace}
				makeReactions={this.makeReactions}
				resetFormReactions={this.resetFormReactions}
				loadPage={this.loadPage}
				fillEmptyRelations={this.fillEmptyRelations}
				validate={this.validate}
				prepareModelForPost={this.prepareModelForPost}
				store={this.props.store}
				getChildMethod={this.props.getChildMethod}
				parentRoute="/partnerContracts/"
				handlers={this.props.handlers}>
				{(childrenProps) => (
					<PartnerContractForm removeNavigationRoute={this.props.removeNavigationRoute} partnerContractFormStore={this.props.store} navigationRoute={this.props.navigationRoute} goTo={this.props.goTo} childrenProps={childrenProps} />
				)}
			</NatDirectoryFormContainer>
		);
	}
}

export default UseLocation(PartnerContractFormContainer);
